import React, { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import './boutique-step.css';
import withNavigation from '../../higher-order/navigation';
import BoutiquePage from '../../boutique/boutique-page';
import Stepper from '../../stepper/Stepper';
import { useHasCampJourInCart } from './hooks/useHasCampJourInCart';
import { onAddUpdateShoppingCart } from '../../actions/shop';
import { getStepNavigationLabels, StepNavigation } from './step-shared';

const CHILD_STEPPER_CONFIG = {
  activeColor: 'var(--primary-color)',
  completeColor: 'var(--medium-gray)',
  circleFontSize: 18,
  titleFontSize: 18,
};

/**
 * Composant d'étape personnalisé pour la Boutique dans le formulaire Camp de Jour
 * Props:
 * - currentStep: index de l'étape actuelle
 * - totalSteps: nombre total d'étapes
 * - goToNextStep: fonction pour aller à l'étape suivante
 * - goToPreviousStep: fonction pour aller à l'étape précédente
 * - formData: objet de données du formulaire actuel
 * - match: objet match de React Router
 * - language: langue actuelle
 */
const BoutiqueStep = ({
  currentStep,
  totalSteps,
  goToNextStep,
  goToPreviousStep,
  resetStepperAndGoToFirstStep,
  formData,
  language,
  dispatch,
}) => {
  const hasCampJourInCart = useHasCampJourInCart();

  const nombreEnfants = parseInt(formData?.nombreEnfants, 10) || 0;

  const [currentChildIndex, setCurrentChildIndex] = useState(0);

  useEffect(() => {
    if (currentChildIndex >= nombreEnfants && nombreEnfants > 0) {
      setCurrentChildIndex(Math.max(0, nombreEnfants - 1));
    }
  }, [nombreEnfants, currentChildIndex]);

  const childStepperSteps = useMemo(() => {
    if (nombreEnfants <= 0) return [];
    return Array.from({ length: nombreEnfants }, (_, i) => ({
      title: language === 'fr' ? `Enfant ${i + 1}` : `Child ${i + 1}`,
    }));
  }, [nombreEnfants, language]);

  const handleChildStepChange = (stepId) => {
    if (stepId >= 0 && stepId < nombreEnfants) {
      setCurrentChildIndex(stepId);
    }
  };

  const t = {
    boutiqueTitle: language === 'fr' ? 'Boutique' : 'Shop',
    ...getStepNavigationLabels(language),
  };

  const handleNext = () => {
    if (nombreEnfants > 0 && currentChildIndex < nombreEnfants - 1) {
      setCurrentChildIndex(currentChildIndex + 1);
    } else {
      if (!hasCampJourInCart && resetStepperAndGoToFirstStep) {
        resetStepperAndGoToFirstStep();
      } else {
        goToNextStep();
      }
    }
  };

  const currentChildLabel = nombreEnfants > 0
    ? (language === 'fr' ? `Enfant ${currentChildIndex + 1}` : `Child ${currentChildIndex + 1}`)
    : null;

  return (
    <div className="boutique-step">
      <h2 className="boutique-step-title">
        {t.boutiqueTitle}
        {currentChildLabel != null && (
          <span className="boutique-step-title-child"> — {currentChildLabel}</span>
        )}
      </h2>

      {nombreEnfants > 1 && childStepperSteps.length > 0 && (
        <div className="boutique-step-child-stepper">
          <Stepper
            steps={childStepperSteps}
            activeStep={currentChildIndex}
            maximalStepReached={nombreEnfants - 1}
            onClick={handleChildStepChange}
            activeColor={CHILD_STEPPER_CONFIG.activeColor}
            completeColor={CHILD_STEPPER_CONFIG.completeColor}
            circleFontSize={CHILD_STEPPER_CONFIG.circleFontSize}
            titleFontSize={CHILD_STEPPER_CONFIG.titleFontSize}
          />
        </div>
      )}

      <div className="boutique-step-embedded">
        <BoutiquePage
          key={currentChildIndex} // reload la boutique (ne reset pas les produits optionnels si en commentaire)
          currentChildIndex={currentChildIndex}
          showPaginationMinimum={true}
          onAddUpdateShoppingCart={(boutiqueItem) => dispatch(onAddUpdateShoppingCart(currentChildIndex, boutiqueItem))}
          backgroundSvgColor="rgb(247, 247, 247)"
        />
      </div>

      <StepNavigation
        currentStep={currentStep}
        totalSteps={totalSteps}
        onPrevious={goToPreviousStep}
        onNext={handleNext}
        labels={t}
        nextAlwaysVisible
        actionsClassName="boutique-step-actions"
        previousClassName="boutique-step-previous-button"
        nextClassName="boutique-step-next-button"
      >
        <button
          type="button"
          className="boutique-step-skip-button"
          onClick={goToNextStep}
        >
          {t.skip}
        </button>
      </StepNavigation>
    </div>
  );
};

BoutiqueStep.validate = (formData, language = 'fr') => {
  return {
    isValid: true,
    errors: {}
  };
};

export default connect()(withNavigation(BoutiqueStep));